import { FC } from 'react'
import { Box, Chip, Grow, IconButton, Paper, Tooltip, Typography, alpha } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useTranslate } from 'react-admin'
import { FLOW, getCategoryIcon } from './tokens'

export const Connector: FC<{ color?: string; dashed?: boolean }> = ({ color = FLOW.connector, dashed }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
    <Box sx={{
      width: 2,
      height: 32,
      background: dashed
        ? `repeating-linear-gradient(to bottom, ${color} 0px, ${color} 6px, transparent 6px, transparent 12px)`
        : color,
      borderRadius: 1,
    }} />
  </Box>
)

export const AddNodeButton: FC<{ onClick: () => void; label: string; color: string }> = ({ onClick, label, color }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Connector dashed />
    <Tooltip title={label} placement="right">
      <IconButton
        onClick={onClick}
        sx={{
          'width': 36,
          'height': 36,
          'border': `2px dashed ${color}`,
          'borderRadius': '50%',
          color,
          'bgcolor': alpha(color, 0.04),
          'transition': 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
          '&:hover': {
            bgcolor: alpha(color, 0.12),
            borderColor: color,
            transform: 'scale(1.12)',
          },
        }}
      >
        <AddIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
    <Connector dashed />
  </Box>
)

interface FlowNodeProps {
  type: 'trigger' | 'action'
  index: number
  label: string
  summary: string
  categoryIcon: string
  onRemove: () => void
  animDelay?: number
}

export const FlowNode: FC<FlowNodeProps> = ({ type, index, label, summary, categoryIcon, onRemove, animDelay = 0 }) => {
  const translate = useTranslate()
  const isTrigger = type === 'trigger'
  const accentColor = isTrigger ? FLOW.blue : FLOW.green
  const bgColor = isTrigger ? FLOW.blueBg : FLOW.greenBg
  const borderColor = isTrigger ? FLOW.blueBorder : FLOW.greenBorder

  return (
    <Grow in timeout={400} style={{ transitionDelay: `${animDelay}ms` }}>
      <Paper
        elevation={0}
        sx={{
          'width': FLOW.nodeWidth,
          'borderRadius': FLOW.cardRadius,
          'border': `1px solid ${borderColor}`,
          'borderLeft': `4px solid ${accentColor}`,
          'bgcolor': bgColor,
          'overflow': 'hidden',
          'transition': 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
          '&:hover': {
            borderColor: accentColor,
            boxShadow: `0 2px 12px ${alpha(accentColor, 0.12)}`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 2, gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            bgcolor: alpha(accentColor, 0.10),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {getCategoryIcon(categoryIcon, { color: accentColor })}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Chip
                label={isTrigger ? translate('automationRule.flow.triggerChip') : translate('automationRule.flow.actionChip')}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  bgcolor: alpha(accentColor, 0.12),
                  color: accentColor,
                  borderRadius: '4px',
                }}
              />
              <Typography variant="caption" sx={{ color: FLOW.gray, fontWeight: 500 }}>
                #{index + 1}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{
              fontWeight: 600, color: 'rgba(0,0,0,0.82)',
              lineHeight: 1.35, mb: 0.25,
            }}>
              {label}
            </Typography>
            <Typography variant="caption" sx={{
              color: 'rgba(0,0,0,0.5)', lineHeight: 1.3,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {summary}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              'color': 'rgba(0,0,0,0.25)',
              'flexShrink': 0,
              '&:hover': { color: FLOW.red, bgcolor: alpha(FLOW.red, 0.08) },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Paper>
    </Grow>
  )
}
